import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Check, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getPhotoUrl } from '@/utils/photoUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
};

const DetallePublicacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehiculo, setVehiculo] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [revendedores, setRevendedores] = useState({});
  const [loading, setLoading] = useState(true);
  const [ofertaToAccept, setOfertaToAccept] = useState(null);
  const [ofertaToReject, setOfertaToReject] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [vehiculoRes, ofertasRes, revendedoresRes] = await Promise.all([
        axios.get(`${API}/vehiculos/${id}`),
        axios.get(`${API}/ofertas/vehiculo/${id}`),
        axios.get(`${API}/revendedores?includeDeleted=true`),
      ]);

      setVehiculo(vehiculoRes.data);
      setOfertas(ofertasRes.data);

      const revMap = {};
      revendedoresRes.data.forEach((r) => {
        revMap[r.id] = r;
      });
      setRevendedores(revMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOffer = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      loadData();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    }
    setOfertaToAccept(null);
  };

  const handleRejectOffer = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      loadData();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    }
    setOfertaToReject(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!vehiculo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="text-muted-foreground">Vehículo no encontrado</div>
        <Button onClick={() => navigate('/admin/publicaciones')}>Volver</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/admin/publicaciones')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Publicaciones
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">
                {vehiculo.marca} {vehiculo.modelo}
              </h1>
              <p className="text-muted-foreground mt-1">
                {vehiculo.version} • {vehiculo.anio} • {vehiculo.km.toLocaleString()} km
              </p>
            </div>
            <Badge variant={badgeVariants[vehiculo.estadoPublicacion]} data-testid="listing-status-badge">
              {vehiculo.estadoPublicacion}
            </Badge>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Left Column: Photos and Details */}
          <div className="space-y-6">
            {/* Image Carousel */}
            <Card data-testid="image-carousel">
              <CardContent className="p-6">
                {vehiculo.fotos && vehiculo.fotos.length > 0 ? (
                  <Carousel className="w-full">
                    <CarouselContent>
                      {vehiculo.fotos.map((foto, index) => (
                        <CarouselItem key={index}>
                          <div className="aspect-video rounded-md overflow-hidden bg-muted">
                            <img
                              src={getPhotoUrl(foto)}
                              alt={`Foto ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-4" />
                    <CarouselNext className="right-4" />
                  </Carousel>
                ) : (
                  <div className="aspect-video rounded-md bg-muted flex items-center justify-center">
                    <p className="text-muted-foreground">Sin fotos</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card>
              <CardHeader>
                <CardTitle>Detalles del Vehículo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Marca:</span>{' '}
                    <span className="font-medium">{vehiculo.marca}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Modelo:</span>{' '}
                    <span className="font-medium">{vehiculo.modelo}</span>
                  </div>
                  {vehiculo.version && (
                    <div>
                      <span className="text-muted-foreground">Versión:</span>{' '}
                      <span className="font-medium">{vehiculo.version}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Año:</span>{' '}
                    <span className="font-medium">{vehiculo.anio}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Kilómetros:</span>{' '}
                    <span className="font-medium">{vehiculo.km.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Combustible:</span>{' '}
                    <span className="font-medium">{vehiculo.tipoCombustible}</span>
                  </div>
                  {vehiculo.cilindrada && (
                    <div>
                      <span className="text-muted-foreground">Cilindrada:</span>{' '}
                      <span className="font-medium">{vehiculo.cilindrada}</span>
                    </div>
                  )}
                  {vehiculo.estadoGeneral && (
                    <div>
                      <span className="text-muted-foreground">Estado:</span>{' '}
                      <span className="font-medium">{vehiculo.estadoGeneral}</span>
                    </div>
                  )}
                  {vehiculo.unicoDueno !== null && (
                    <div>
                      <span className="text-muted-foreground">Único dueño:</span>{' '}
                      <span className="font-medium">{vehiculo.unicoDueno ? 'Sí' : 'No'}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Autor:</span>{' '}
                    <span className="font-medium">{vehiculo.autor}</span>
                  </div>
                </div>

                {vehiculo.comentarios && (
                  <div className="mt-4">
                    <span className="text-sm text-muted-foreground">Comentarios:</span>
                    <p className="text-sm mt-1">{vehiculo.comentarios}</p>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t">
                  <div className="text-2xl font-bold text-primary">
                    ${vehiculo.precioBase.toLocaleString('en-US')}
                  </div>
                  {vehiculo.precioAdjudicado && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Adjudicado por: ${vehiculo.precioAdjudicado.toLocaleString('en-US')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Offers */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas Recibidas</CardTitle>
              </CardHeader>
              <CardContent data-testid="offers-list">
                {ofertas.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No hay ofertas aún
                  </p>
                ) : (
                  <div className="space-y-3">
                    {ofertas.map((oferta) => {
                      const revendedor = revendedores[oferta.revendedorId];
                      return (
                        <Card key={oferta.id} className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium">
                                {revendedor?.nombre || 'Revendedor'}
                                {revendedor?.deletedAt && (
                                  <span className="ml-2 text-xs text-muted-foreground" title={`Eliminado el ${new Date(revendedor.deletedAt).toLocaleDateString('es-UY')}`}>
                                    (eliminado)
                                  </span>
                                )}
                              </div>
                              {revendedor?.empresa && (
                                <div className="text-xs text-muted-foreground">
                                  {revendedor.empresa}
                                </div>
                              )}
                            </div>
                            <Badge variant={badgeVariants[oferta.estado]}>
                              {oferta.estado}
                            </Badge>
                          </div>

                          <div className="text-xl font-bold text-primary mb-1">
                            ${oferta.monto.toLocaleString('en-US')}
                          </div>

                          <div className="text-xs text-muted-foreground mb-3">
                            {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}
                          </div>

                          {oferta.estado === 'Pendiente' && vehiculo.estadoPublicacion !== 'Adjudicada' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1"
                                onClick={() => setOfertaToAccept(oferta.id)}
                                data-testid="accept-offer-button"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Aceptar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setOfertaToReject(oferta.id)}
                                data-testid="reject-offer-button"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          )}

                          {revendedor && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2"
                              asChild
                            >
                              <Link to="/admin/revendedores">
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Revendedor
                              </Link>
                            </Button>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Accept Offer Dialog */}
      <AlertDialog open={!!ofertaToAccept} onOpenChange={() => setOfertaToAccept(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aceptar oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              Al aceptar esta oferta, el vehículo se marcará como adjudicado y las demás ofertas
              pendientes serán rechazadas automáticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleAcceptOffer(ofertaToAccept)}>
              Aceptar Oferta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Offer Dialog */}
      <AlertDialog open={!!ofertaToReject} onOpenChange={() => setOfertaToReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción marcará la oferta como rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleRejectOffer(ofertaToReject)}>
              Rechazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DetallePublicacion;